import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Heart Failure Risk Study
 *
 * Trial IDs: VT2DMD6973C00001, CT2DMD6973C00001, DMT2DMD6973C00001
 *
 * Target population: Adults 40+ with Type 2 Diabetes and cardiovascular disease at risk of heart failure
 *
 * Key inclusion criteria:
 * - Age 40+
 * - Type 2 Diabetes diagnosis with medication management (tablet or injection)
 * - History of cardiovascular disease (stroke, heart attack, CAD, PAD, CKD, AFib, hypertension)
 *
 * Key exclusion criteria:
 * - Heart failure diagnosis
 * - Type 1 diabetes
 * - Type 2 diabetes managed only with diet and lifestyle (no medications)
 * - End-stage kidney disease/dialysis
 * - Recent cardiovascular event (<3 months)
 * - Liver failure/cirrhosis
 * - Organ transplant
 * - Specific medications (MRA, carbamazepine, phenytoin, potassium-sparing diuretics, aliskiren)
 * - Current clinical trial participation
 */
export const heartFailureRiskScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "40_or_older", label: "40 or older" },
      {
        value: "under_40",
        label: "39 or under",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for adults who are 40 years of age or older.",
    attributeMapping: mappers.value({
      "40_or_older": [],
      under_40: [],
    }),
  },

  {
    id: "diabetes_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with type 2 diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type2_with_meds",
        label: "Yes, I have type 2 diabetes and I manage it with medication (tablet or injection)",
      },
      {
        value: "type2_diet_lifestyle",
        label: "Yes, I have type 2 diabetes and I manage it with diet and lifestyle",
        disqualify: true,
      },
      {
        value: "no_type2",
        label: "No, I do not have type 2 diabetes",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants with type 2 diabetes who are currently taking medication.",
    attributeMapping: mappers.value({
      type2_with_meds: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
      type2_diet_lifestyle: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
      no_type2: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "type1_diabetes_exclusion",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with type 1 diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_type1",
        label: "Yes, I have been diagnosed with type 1 diabetes",
        disqualify: true,
      },
      {
        value: "no_type1",
        label: "No, I have not been diagnosed with type 1 diabetes",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with type 1 diabetes.",
    attributeMapping: mappers.value({
      yes_type1: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      no_type1: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "hypertension",
    type: QuestionType.RADIO,
    question: "Do you have high blood pressure or are you currently taking blood pressure medication?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_hypertension",
        label: "Yes, I have high blood pressure or take blood pressure medication",
      },
      {
        value: "no_hypertension",
        label: "No, I do not have high blood pressure and am not taking blood pressure medication",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants to have high blood pressure or be taking blood pressure medication.",
    attributeMapping: mappers.value({
      yes_hypertension: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
      ],
      no_hypertension: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "cardiovascular_or_renal_history",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "prior_stroke",
        label: "I have had a stroke (this does not include \"mini-strokes\")",
      },
      {
        value: "heart_attack",
        label: "I have had a heart attack",
      },
      {
        value: "coronary_artery_disease",
        label: "I have coronary artery disease or a history of coronary revascularization (e.g., stent, bypass)",
      },
      {
        value: "peripheral_arterial_disease",
        label: "I have peripheral arterial disease (narrowing of the arteries in the legs)",
      },
      {
        value: "carotid_artery_narrowing",
        label: "I have carotid artery narrowing that required surgery or stenting",
      },
      {
        value: "none_of_above",
        label: "None of the above applies to me",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants to have at least one cardiovascular condition.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        prior_stroke: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
        heart_attack: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        coronary_artery_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        peripheral_arterial_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
        carotid_artery_narrowing: [
          { attributeDefinitionId: 25084, booleanAttributeValue: true }, // has_history_carotid_or_peripheral_artery_revascularization
        ],
      },
      noneValue: "none_of_above",
      noneAttributeMap: {
        none_of_above: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 25084, booleanAttributeValue: false }, // has_history_carotid_or_peripheral_artery_revascularization
        ],
      },
    }),
  },

  {
    id: "additional_risk_factors",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following additional risk factors apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "chronic_kidney_disease",
        label: "I have chronic kidney disease or protein in my urine",
      },
      {
        value: "atrial_fibrillation",
        label: "I have atrial fibrillation or atrial flutter",
      },
      {
        value: "age_70_or_older",
        label: "I am 70 years of age or older",
      },
      {
        value: "none_of_above",
        label: "None of the above applies to me",
        disqualify: (allAnswers: Record<string, string | string[] | number | null | undefined>) => {
          // Disqualify ONLY if they have less than 2 CV conditions
          const cvConditions = allAnswers?.["cardiovascular_or_renal_history"] as string[];
          if (!Array.isArray(cvConditions)) return true;

          const conditionCount = cvConditions.filter(c => c !== "none_of_above").length;
          return conditionCount < 2; // Disqualify if they have 0 or 1 CV conditions
        },
      },
    ],
    disqualifyMessage:
      "This study requires participants to have either 2+ cardiovascular conditions OR at least one additional risk factor.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        atrial_fibrillation: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_IRREGULAR_HEART_RHYTHM, booleanAttributeValue: true },
        ],
        age_70_or_older: [],
      },
      noneValue: "none_of_above",
      noneAttributeMap: {
        none_of_above: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_IRREGULAR_HEART_RHYTHM, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "heart_failure_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with heart failure?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_heart_failure",
        label: "Yes, I have been diagnosed with heart failure",
        disqualify: true,
      },
      {
        value: "no_heart_failure",
        label: "No, I have not been diagnosed with heart failure",
      },
    ],
    disqualifyMessage:
      "This study cannot include people who have already been diagnosed with heart failure.",
    attributeMapping: mappers.value({
      yes_heart_failure: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
      ],
      no_heart_failure: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "kidney_disease_severity",
    type: QuestionType.RADIO,
    question: "Do you have end-stage kidney disease or are you on dialysis?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_dialysis",
        label: "Yes, I have end-stage kidney disease or am on dialysis",
        disqualify: true,
      },
      {
        value: "no_dialysis",
        label: "No, I do not have end-stage kidney disease and am not on dialysis",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with end-stage kidney disease or those on dialysis.",
    attributeMapping: mappers.value({
      yes_dialysis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
      ],
      no_dialysis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_cardiovascular_event",
    type: QuestionType.RADIO,
    question: "Have you had a stroke, heart attack, or other cardiovascular event in the last 3 months?",
    helpText: "This includes heart surgery, carotid artery surgery, stent placement, or bypass surgery.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_recent_event",
        label: "Yes, I have had a cardiovascular event in the last 3 months",
        disqualify: true,
      },
      {
        value: "no_recent_event",
        label: "No, I have not had a cardiovascular event in the last 3 months",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include people who have had a recent cardiovascular event within the past 3 months.",
    attributeMapping: mappers.value({
      yes_recent_event: [],
      no_recent_event: [],
    }),
  },

  {
    id: "serious_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had an organ transplant or am on a transplant waiting list",
        disqualify: true,
      },
      {
        value: "pregnant_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "none_of_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with these serious medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        liver_failure_cirrhosis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        pregnant_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_of_above",
      noneAttributeMap: {
        none_of_above: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "restricted_medications",
    type: QuestionType.CHECKBOX,
    question: "Are you currently taking any of the following medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "spironolactone",
        label: "Spironolactone (Aldactone)",
        disqualify: true,
      },
      {
        value: "eplerenone",
        label: "Eplerenone (Inspra)",
        disqualify: true,
      },
      {
        value: "carbamazepine",
        label: "Carbamazepine (Tegretol, Carbatrol)",
        disqualify: true,
      },
      {
        value: "phenytoin",
        label: "Phenytoin (Dilantin)",
        disqualify: true,
      },
      {
        value: "amiloride",
        label: "Amiloride (Midamor, Moduretic)",
        disqualify: true,
      },
      {
        value: "triamterene",
        label: "Triamterene (Dyrenium)",
        disqualify: true,
      },
      {
        value: "aliskiren",
        label: "Aliskiren (Tekturna)",
        disqualify: true,
      },
      {
        value: "none_of_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "This study cannot include people currently taking these medications.",
    attributeMapping: mappers.value({
      spironolactone: [],
      eplerenone: [],
      carbamazepine: [],
      phenytoin: [],
      amiloride: [],
      triamterene: [],
      aliskiren: [],
      none_of_above: [],
    }),
  },

  {
    id: "a1c_check",
    type: QuestionType.RADIO,
    question: "If you have had an A1c test recently, was your A1c level less than 11.5%?",
    helpText: "A1c is a measure of blood sugar control. If you haven't had this test or don't know your result, please select 'I don't know my A1c level'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "a1c_under_11_5",
        label: "Yes, my A1c was less than 11.5%",
      },
      {
        value: "a1c_over_11_5",
        label: "No, my A1c was 11.5% or higher",
        disqualify: true,
      },
      {
        value: "a1c_unknown",
        label: "I don't know my A1c level",
      },
    ],
    disqualifyMessage:
      "This study requires participants with A1c levels below 11.5%.",
    attributeMapping: mappers.value({
      a1c_under_11_5: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
      ],
      a1c_over_11_5: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
      ],
      a1c_unknown: [],
    }),
  },

  {
    id: "current_clinical_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    helpText: "This includes any clinical trial participation within the past 30 days.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_in_trial",
        label: "Yes, I am currently enrolling in or participating in another clinical trial",
        disqualify: true,
      },
      {
        value: "no_not_in_trial",
        label: "No, I am not currently enrolling in or participating in another clinical trial",
      },
    ],
    disqualifyMessage:
      "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes_in_trial: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      no_not_in_trial: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "study_visit_availability",
    type: QuestionType.RADIO,
    question: "Are you willing to attend regular in-person study visits?",
    helpText: "This study involves regular in-person visits at a research site near you.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_willing",
        label: "Yes, I am willing to attend in-person visits",
      },
      {
        value: "no_cannot_commit",
        label: "No, I cannot commit to in-person visits",
        disqualify: true,
      },
      {
        value: "discuss_schedule",
        label: "I would like to discuss the schedule further with the study team",
      },
    ],
    disqualifyMessage:
      "This study requires regular in-person visits at a research site.",
    attributeMapping: mappers.value({
      yes_willing: [],
      no_cannot_commit: [],
      discuss_schedule: [],
    }),
  },
];
